/**
 * The one save confirmation.
 *
 * Winnie, 15 September: "every save action anywhere in the platform to shows 'Your changes have
 * been successfully saved.' as an inline toast. Direct Auto's own Cost of Sales screen already
 * shows 'Expense updated.' as a green banner - the pattern exists, it's just not applied
 * everywhere yet."
 *
 * Before this, roughly twenty screens each confirmed a save in their own way: a green banner on
 * Cost of Sales, a floating toast that only existed inside the Admin view, a line of text under
 * a button, and on several screens nothing at all - the button simply stopped saying "Saving…".
 * Three of those are invisible to anyone not looking at the right corner of the screen.
 *
 * Deliberately a module singleton with a subscriber list rather than a React context. A context
 * would only be reachable from components underneath its provider, and roughly half the save
 * paths in this app finish inside a service call or an async handler that has no component to
 * hold a hook. `toast.saved()` can be called from anywhere, including a `.then()`.
 *
 * The standard sentence is for a SAVE. Things that are not saves keep their own words: "a
 * set-password email has been sent", "sent - see the response below", a validation refusal.
 * Making those say "your changes have been successfully saved" would be the same failure the
 * consistency rule is trying to fix, in the other direction.
 */
import { useEffect, useState } from 'react'
import './Toast.css'

export const SAVED_MESSAGE = 'Your changes have been successfully saved.'

export type ToastKind = 'ok' | 'error'

export interface ToastItem {
  id: number
  message: string
  kind: ToastKind
}

const OK_MS = 3500
/* An error stays up longer because it usually needs reading, not noticing. */
const ERROR_MS = 6500
const MAX_VISIBLE = 3

let seq = 0
let items: ToastItem[] = []
const listeners = new Set<(items: ToastItem[]) => void>()

function emit() {
  const snapshot = items
  listeners.forEach((fn) => fn(snapshot))
}

function dismiss(id: number) {
  if (!items.some((t) => t.id === id)) return
  items = items.filter((t) => t.id !== id)
  emit()
}

function push(message: string, kind: ToastKind): number {
  const text = String(message || '').trim()
  if (!text) return -1

  /* A save that also triggers a reload can land the same sentence twice within a few
     milliseconds. Two identical toasts stacked on top of each other read as a bug. */
  const duplicate = items.find((t) => t.message === text && t.kind === kind)
  if (duplicate) return duplicate.id

  const item: ToastItem = { id: ++seq, message: text, kind }
  items = [...items, item].slice(-MAX_VISIBLE)
  emit()
  window.setTimeout(() => dismiss(item.id), kind === 'error' ? ERROR_MS : OK_MS)
  return item.id
}

export const toast = {
  /** The standard confirmation. Every save action in the platform ends here. */
  saved: () => push(SAVED_MESSAGE, 'ok'),
  /** A confirmation that is genuinely not a save (an email sent, a test message accepted). */
  show: (message: string) => push(message, 'ok'),
  error: (message: string) => push(message, 'error'),
  dismiss,
  /** Test seam — the QA suite drives the queue without a browser. */
  _peek: (): ToastItem[] => items,
  _reset: () => {
    items = []
    emit()
  },
}

/**
 * Mounted once, at the app root, so it covers every screen including the buyer and seller
 * pages that render outside the dashboard shell.
 */
export default function ToastHost() {
  const [visible, setVisible] = useState<ToastItem[]>(items)

  useEffect(() => {
    const fn = (next: ToastItem[]) => setVisible(next)
    listeners.add(fn)
    setVisible(items)
    return () => {
      listeners.delete(fn)
    }
  }, [])

  if (!visible.length) return null

  return (
    <div className="da-toast-host" role="status" aria-live="polite">
      {visible.map((t) => (
        <div
          key={t.id}
          className={`da-toast ${t.kind === 'error' ? 'da-toast-error' : 'da-toast-ok'}`}
          onClick={() => dismiss(t.id)}
        >
          <span className="da-toast-mark">{t.kind === 'error' ? '✕' : '✓'}</span>
          <span className="da-toast-text">{t.message}</span>
        </div>
      ))}
    </div>
  )
}
